<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import WhatsappGeneralInfoStep from './WhatsappGeneralInfoStep.vue';
import WhatsappMessageBodyStep from './WhatsappMessageBodyStep.vue';
import WhatsappMediaHeaderStep from './WhatsappMediaHeaderStep.vue';
import WhatsappQuickReplyStep from './WhatsappQuickReplyStep.vue';
import WhatsappCallToActionStep from './WhatsappCallToActionStep.vue';

defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  // Onboarding submits without the file, so it turns the optional quick reply media off.
  allowQuickReplyMedia: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['submit', 'cancel']);

// Template types whose header can carry an image, video or document.
const MEDIA_HEADER_TYPES = ['MEDIA', 'QUICK_REPLY'];

const { t } = useI18n();

const currentStep = ref(1);
const wizardData = reactive({
  name: '',
  language: 'pt_BR',
  category: 'UTILITY',
  template_type: 'TEXT',
  body: '',
  footer: '',
  body_examples: [],
  // Media fields
  header: {
    format: '',
  },
  // Button fields (for future)
  buttons: [],
  // Authentication fields (for future)
  otp_type: 'COPY_CODE',
  add_security_recommendation: true,
  code_expiration_minutes: 10,
});

// File kept outside reactive to avoid Proxy on native File object
const mediaFile = ref(null);

// A file picked for one type must not ride along into another.
watch(
  () => wizardData.template_type,
  () => {
    mediaFile.value = null;
    wizardData.header = { format: '' };
  }
);

const needsStep3 = computed(() => {
  return ['MEDIA', 'QUICK_REPLY', 'CALL_TO_ACTION', 'AUTHENTICATION'].includes(
    wizardData.template_type
  );
});

const step3Title = computed(() => {
  switch (wizardData.template_type) {
    case 'MEDIA':
      return t('WHATSAPP_TEMPLATES.CREATE.MEDIA.TYPE_LABEL');
    case 'QUICK_REPLY':
      return t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.STEP_TITLE');
    case 'CALL_TO_ACTION':
      return t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.STEP_TITLE');
    case 'AUTHENTICATION':
      return t('WHATSAPP_TEMPLATES.CREATE.AUTHENTICATION.OTP_TYPE_LABEL');
    default:
      return t('WHATSAPP_TEMPLATES.CREATE.WIZARD.STEP_3_TITLE');
  }
});

const stepCircleClass = step => {
  const isActive = currentStep.value === step;
  const isCompleted = currentStep.value > step;

  return [
    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all',
    isActive || isCompleted
      ? 'bg-n-brand text-white'
      : 'bg-n-slate-6 text-n-slate-11',
  ];
};

const stepLabelClass = step => {
  const isActive = currentStep.value === step;

  return [
    'text-sm font-medium transition-all hidden sm:inline',
    isActive ? 'text-n-slate-12' : 'text-n-slate-11',
  ];
};

const stepLineClass = stepNumber => {
  return [
    'w-12 sm:w-16 h-0.5 transition-all',
    currentStep.value > stepNumber ? 'bg-n-brand' : 'bg-n-slate-6',
  ];
};

const handleStep1Update = data => {
  Object.assign(wizardData, data);
};

const handleStep1Next = () => {
  currentStep.value = 2;
};

const handleBodyUpdate = value => {
  wizardData.body = value;
};

const handleFooterUpdate = value => {
  wizardData.footer = value;
};

const handleBodyExamplesUpdate = value => {
  wizardData.body_examples = value;
};

const handleCancel = () => emit('cancel');

const handleSubmit = () => {
  const formData = {
    name: wizardData.name,
    language: wizardData.language,
    category: wizardData.category,
    template_type: wizardData.template_type,
    body: wizardData.body,
  };

  // Add footer if present
  if (wizardData.footer) {
    formData.footer = wizardData.footer;
  }

  // Add body examples if there are variables
  if (wizardData.body_examples.length > 0) {
    formData.body_examples = wizardData.body_examples;
  }

  // Add media header (format only — file is sent separately); optional on QUICK_REPLY
  const hasMediaHeader =
    MEDIA_HEADER_TYPES.includes(wizardData.template_type) &&
    Boolean(wizardData.header.format);
  if (hasMediaHeader) {
    formData.header = {
      format: wizardData.header.format,
    };
  }

  // Add buttons for QUICK_REPLY and CALL_TO_ACTION templates
  if (
    ['QUICK_REPLY', 'CALL_TO_ACTION'].includes(wizardData.template_type) &&
    wizardData.buttons.length > 0
  ) {
    formData.buttons = wizardData.buttons;
  }

  // Add authentication fields for AUTHENTICATION templates
  if (wizardData.template_type === 'AUTHENTICATION') {
    formData.otp_type = wizardData.otp_type;
    formData.add_security_recommendation =
      wizardData.add_security_recommendation;
    formData.code_expiration_minutes = wizardData.code_expiration_minutes;
  }

  const fileToSubmit = hasMediaHeader ? mediaFile.value : null;
  emit('submit', formData, fileToSubmit);
};

const handleStep2Submit = () => {
  if (needsStep3.value) {
    currentStep.value = 3;
  } else {
    handleSubmit();
  }
};

const handleStep2Back = () => {
  currentStep.value = 1;
};

const handleHeaderUpdate = value => {
  wizardData.header = { ...wizardData.header, ...value };
};

const handleMediaFileUpdate = file => {
  mediaFile.value = file;
};

const handleButtonsUpdate = value => {
  wizardData.buttons = [...value];
};

const handleStep3Submit = () => {
  handleSubmit();
};

const handleStep3Back = () => {
  currentStep.value = 2;
};

const reset = () => {
  currentStep.value = 1;
  mediaFile.value = null;
  Object.assign(wizardData, {
    name: '',
    language: 'pt_BR',
    category: 'UTILITY',
    template_type: 'TEXT',
    body: '',
    footer: '',
    body_examples: [],
    header: {
      format: '',
    },
    buttons: [],
    otp_type: 'COPY_CODE',
    add_security_recommendation: true,
    code_expiration_minutes: 10,
  });
};

defineExpose({ reset });
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Progress Stepper -->
    <nav
      :aria-label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.PROGRESS_LABEL')"
      class="flex items-center justify-center gap-2"
    >
      <!-- Step 1: General Information -->
      <div
        :aria-current="currentStep === 1 ? 'step' : undefined"
        role="group"
        class="flex items-center gap-2"
      >
        <div
          :class="stepCircleClass(1)"
          :aria-label="
            t('WHATSAPP_TEMPLATES.CREATE.WIZARD.STEP_LABEL', { step: 1 })
          "
        >
          {{ 1 }}
        </div>
        <span :class="stepLabelClass(1)">
          {{ t('WHATSAPP_TEMPLATES.CREATE.WIZARD.STEP_1_TITLE') }}
        </span>
      </div>

      <div :class="stepLineClass(1)" aria-hidden="true" />

      <!-- Step 2: Message Body -->
      <div
        :aria-current="currentStep === 2 ? 'step' : undefined"
        role="group"
        class="flex items-center gap-2"
      >
        <div
          :class="stepCircleClass(2)"
          :aria-label="
            t('WHATSAPP_TEMPLATES.CREATE.WIZARD.STEP_LABEL', { step: 2 })
          "
        >
          {{ 2 }}
        </div>
        <span :class="stepLabelClass(2)">
          {{ t('WHATSAPP_TEMPLATES.CREATE.WIZARD.STEP_2_TITLE') }}
        </span>
      </div>

      <!-- Step 3: Type-specific (conditional) -->
      <template v-if="needsStep3">
        <div :class="stepLineClass(2)" aria-hidden="true" />

        <div
          :aria-current="currentStep === 3 ? 'step' : undefined"
          role="group"
          class="flex items-center gap-2"
        >
          <div
            :class="stepCircleClass(3)"
            :aria-label="
              t('WHATSAPP_TEMPLATES.CREATE.WIZARD.STEP_LABEL', { step: 3 })
            "
          >
            {{ 3 }}
          </div>
          <span :class="stepLabelClass(3)">
            {{ step3Title }}
          </span>
        </div>
      </template>
    </nav>

    <!-- Step Content -->
    <WhatsappGeneralInfoStep
      v-if="currentStep === 1"
      :model-value="wizardData"
      @update:model-value="handleStep1Update"
      @next="handleStep1Next"
      @cancel="handleCancel"
    />

    <WhatsappMessageBodyStep
      v-if="currentStep === 2"
      :body="wizardData.body"
      :footer="wizardData.footer"
      :body-examples="wizardData.body_examples"
      :loading="loading"
      :is-last-step="!needsStep3"
      @update:body="handleBodyUpdate"
      @update:footer="handleFooterUpdate"
      @update:body-examples="handleBodyExamplesUpdate"
      @submit="handleStep2Submit"
      @back="handleStep2Back"
    />

    <!-- Step 3: Type-specific content -->
    <WhatsappMediaHeaderStep
      v-if="currentStep === 3 && wizardData.template_type === 'MEDIA'"
      :media-file="mediaFile"
      :body="wizardData.body"
      :footer="wizardData.footer"
      :body-examples="wizardData.body_examples"
      :loading="loading"
      @update:media-file="handleMediaFileUpdate"
      @update:header="handleHeaderUpdate"
      @update:body-examples="handleBodyExamplesUpdate"
      @submit="handleStep3Submit"
      @back="handleStep3Back"
    />

    <WhatsappQuickReplyStep
      v-if="currentStep === 3 && wizardData.template_type === 'QUICK_REPLY'"
      :buttons="wizardData.buttons"
      :media-file="mediaFile"
      :allow-media="allowQuickReplyMedia"
      :body="wizardData.body"
      :footer="wizardData.footer"
      :body-examples="wizardData.body_examples"
      :loading="loading"
      @update:buttons="handleButtonsUpdate"
      @update:media-file="handleMediaFileUpdate"
      @update:header="handleHeaderUpdate"
      @update:body-examples="handleBodyExamplesUpdate"
      @submit="handleStep3Submit"
      @back="handleStep3Back"
      @cancel="handleCancel"
    />

    <WhatsappCallToActionStep
      v-if="currentStep === 3 && wizardData.template_type === 'CALL_TO_ACTION'"
      :buttons="wizardData.buttons"
      :body="wizardData.body"
      :footer="wizardData.footer"
      :body-examples="wizardData.body_examples"
      :loading="loading"
      @update:buttons="handleButtonsUpdate"
      @submit="handleStep3Submit"
      @back="handleStep3Back"
      @cancel="handleCancel"
    />

    <!-- TODO: Add step 3 component for AUTHENTICATION -->
  </div>
</template>
